/**
 * Single source of truth for weekly team scoring and breakdown.
 */

import { isSimultaneousDoubleEliminationWeek } from './predictionUtils';
import {
  getOrderedEliminationRows,
  getResultRowContestantId,
  getVotedOutNames,
  isOrderedEliminationResults,
  normalizeContestantToName,
  wasContestantEliminatedPreviously,
} from './scoringHelpers';

/**
 * @param {Object} teamData - Team with draftPicks
 * @param {Array} predictions - All prediction docs for this team for this week
 * @param {Object} results - Week results
 * @param {number} weekNumber
 * @param {Object} scoringRules - League-season scoring rules
 * @param {Object|null} seasonData - Season document data
 * @param {Object|null} seasonContestantsMap - contestantId -> name
 * @param {number|null} mergeWeekHint - Optional seasonData.mergeWeek passthrough
 * @returns {Object} Breakdown shape used by UI and persistence
 */
export function computeWeekScore(
  teamData,
  predictions,
  results,
  weekNumber,
  scoringRules,
  seasonData = null,
  seasonContestantsMap = null,
  mergeWeekHint = null,
) {
  let predictionPoints = 0;
  let winnerPointsBonus = 0;

  let seasonMergeWeek = seasonData?.mergeWeek ?? mergeWeekHint ?? null;
  if (seasonMergeWeek === null || seasonMergeWeek === undefined) {
    const mergeWeekObj = seasonData?.weeks?.find(w => w.isMergeWeek === true);
    if (mergeWeekObj) {
      seasonMergeWeek = mergeWeekObj.weekNumber;
    }
  }
  const isPostMerge =
    seasonMergeWeek != null && weekNumber >= seasonMergeWeek;

  const rules = {
    predictionPoints: isPostMerge
      ? (scoringRules?.postMergePredictionPoints ||
          scoringRules?.predictionPoints ||
          1)
      : (scoringRules?.preMergePredictionPoints ||
          scoringRules?.predictionPoints ||
          1),
    survivalPoints: scoringRules?.survivalPoints || 1,
    mergeWeekBonus: scoringRules?.mergeWeekBonus || 0,
    winnerPoints: scoringRules?.winnerPoints || 0,
    ...scoringRules,
  };

  const votedOutNames = getVotedOutNames(results, seasonContestantsMap);

  const isOrderedElimination = isOrderedEliminationResults(results);
  const currentWeekMeta = seasonData?.weeks?.find(
    w => w.weekNumber === weekNumber,
  );

  const draftPicks = teamData?.draftPicks || [];
  let survivingContestants = 0;

  for (const pick of draftPicks) {
    const draftedContestantName = normalizeContestantToName(
      pick.contestantId,
      seasonContestantsMap,
    );

    if (
      seasonData &&
      wasContestantEliminatedPreviously(
        draftedContestantName,
        seasonData,
        weekNumber,
        seasonContestantsMap,
      )
    ) {
      continue;
    }

    if (isOrderedElimination) {
      if (isSimultaneousDoubleEliminationWeek(currentWeekMeta)) {
        if (!votedOutNames.includes(draftedContestantName)) {
          survivingContestants += 2;
        }
      } else {
        const ordered = getOrderedEliminationRows(results);
        let stillInEpisode = true;
        for (const row of ordered) {
          if (!stillInEpisode) break;
          const eliminatedName = normalizeContestantToName(
            row.contestantName || row.contestantId,
            seasonContestantsMap,
          );
          if (eliminatedName === draftedContestantName) {
            stillInEpisode = false;
          } else {
            survivingContestants++;
          }
        }
      }
    } else if (results.winner || results.fireMakingLoser) {
      const winnerName = normalizeContestantToName(
        results.winner,
        seasonContestantsMap,
      );
      const fireMakingLoserName = normalizeContestantToName(
        results.fireMakingLoser,
        seasonContestantsMap,
      );
      const votedOutName = normalizeContestantToName(
        results.votedOut,
        seasonContestantsMap,
      );

      if (votedOutName !== draftedContestantName) {
        survivingContestants++;

        if (fireMakingLoserName !== draftedContestantName) {
          survivingContestants++;

          if (winnerName === draftedContestantName) {
            winnerPointsBonus += rules.winnerPoints || 0;
          }
        }
      }
    } else if (!votedOutNames.includes(draftedContestantName)) {
      survivingContestants++;
    }
  }

  const survivalPoints = survivingContestants * rules.survivalPoints;

  const normalizeToId = (value, idToNameMap) => {
    if (!value) return null;
    if (idToNameMap && idToNameMap[value]) {
      return value;
    }
    if (idToNameMap) {
      const id = Object.keys(idToNameMap).find(
        idKey => idToNameMap[idKey] === value,
      );
      if (id) return id;
    }
    return value;
  };

  if (predictions && predictions.length > 0) {
    for (const prediction of predictions) {
      const predictionData = prediction.predictions || {};
      // Legacy: single ID string saved before week switched to ordered multi-boot
      let votedOutSlots = predictionData.votedOut;
      if (
        isOrderedElimination &&
        typeof votedOutSlots === 'string' &&
        votedOutSlots
      ) {
        const slotCount =
          currentWeekMeta?.eliminationType === 'triple' ? 3 : 2;
        votedOutSlots = [{ contestantId: votedOutSlots, position: 1 }];
        for (let p = 2; p <= slotCount; p++) {
          votedOutSlots.push({ contestantId: null, position: p });
        }
      }

      if (isOrderedElimination && Array.isArray(votedOutSlots)) {
        for (const predItem of votedOutSlots) {
          if (predItem && predItem.contestantId && predItem.position) {
            const predictedContestantId = predItem.contestantId;
            const predictedPosition = predItem.position;

            const actualResult = results.votedOut.find(
              r => r.position === predictedPosition,
            );
            if (actualResult) {
              const actualContestantId = getResultRowContestantId(
                actualResult,
                seasonContestantsMap,
              );

              if (
                actualContestantId &&
                predictedContestantId === actualContestantId
              ) {
                predictionPoints += rules.predictionPoints;
              }
            }
          }
        }
      } else {
        const predictedVotedOutId = predictionData.votedOut
          ? normalizeToId(predictionData.votedOut, seasonContestantsMap)
          : null;
        const predictedWinnerId = predictionData.winner
          ? normalizeToId(predictionData.winner, seasonContestantsMap)
          : null;
        const predictedFireMakingLoserId = predictionData.fireMakingLoser
          ? normalizeToId(predictionData.fireMakingLoser, seasonContestantsMap)
          : null;

        if (predictedVotedOutId) {
          const resultVotedOutId = normalizeToId(
            results.votedOut,
            seasonContestantsMap,
          );
          if (
            resultVotedOutId &&
            (predictedVotedOutId === resultVotedOutId ||
              (Array.isArray(results.votedOut) &&
                results.votedOut.some(r => {
                  const rId = normalizeToId(r, seasonContestantsMap);
                  return rId === predictedVotedOutId;
                })))
          ) {
            predictionPoints += rules.predictionPoints;
          }
        }

        if (results.winner && predictedWinnerId) {
          const resultWinnerId = normalizeToId(
            results.winner,
            seasonContestantsMap,
          );
          if (resultWinnerId === predictedWinnerId) {
            predictionPoints += rules.predictionPoints;
          }
        }

        if (results.fireMakingLoser && predictedFireMakingLoserId) {
          const resultFireMakingLoserId = normalizeToId(
            results.fireMakingLoser,
            seasonContestantsMap,
          );
          if (resultFireMakingLoserId === predictedFireMakingLoserId) {
            predictionPoints += rules.predictionPoints;
          }
        }
      }
    }
  }

  let mergeWeekBonus = 0;
  const isThisMergeWeek =
    seasonMergeWeek != null && weekNumber === seasonMergeWeek;
  if (isThisMergeWeek && rules.mergeWeekBonus > 0) {
    mergeWeekBonus = rules.mergeWeekBonus;
  }

  const total =
    predictionPoints + survivalPoints + winnerPointsBonus + mergeWeekBonus;

  return {
    predictionPoints,
    survivalPoints,
    winnerPointsBonus,
    mergeWeekBonus,
    total,
    breakdown: {
      survivingContestants,
      rules,
    },
  };
}

/**
 * Plain object safe for Firestore team document.
 * @param {Object} result - computeWeekScore output
 * @param {string} isoTimestamp
 * @returns {Object}
 */
export function toPersistedWeekBreakdown(result, isoTimestamp) {
  const r = result.breakdown?.rules || {};
  return {
    predictionPoints: result.predictionPoints,
    survivalPoints: result.survivalPoints,
    winnerPointsBonus: result.winnerPointsBonus,
    mergeWeekBonus: result.mergeWeekBonus,
    total: result.total,
    survivingContestants: result.breakdown?.survivingContestants ?? 0,
    rulesSnapshot: {
      predictionPoints: r.predictionPoints,
      survivalPoints: r.survivalPoints,
      mergeWeekBonus: r.mergeWeekBonus,
      winnerPoints: r.winnerPoints,
    },
    calculatedAt: isoTimestamp,
  };
}

/**
 * Restore UI breakdown shape from Firestore.
 * @param {Object} persisted
 * @returns {Object|null}
 */
export function hydrateWeekScoreBreakdown(persisted) {
  if (!persisted || typeof persisted.total !== 'number') return null;
  return {
    predictionPoints: persisted.predictionPoints ?? 0,
    survivalPoints: persisted.survivalPoints ?? 0,
    winnerPointsBonus: persisted.winnerPointsBonus ?? 0,
    mergeWeekBonus: persisted.mergeWeekBonus ?? 0,
    total: persisted.total,
    breakdown: {
      survivingContestants: persisted.survivingContestants ?? 0,
      rules: persisted.rulesSnapshot
        ? { ...persisted.rulesSnapshot }
        : {},
    },
  };
}
